'use client';

import { MaterialReactTable, type MRT_ColumnDef, type MRT_SortingState } from 'material-react-table';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { Avatar, Box, Link, Tab, Tabs, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useNotification } from '@/contexts/NotificationContext';
import {
  fetchLocationAdjustmentTargets,
  queryLocationInventoryCatalog,
  type LocationAdjustmentWarehouseTarget,
  type LocationCatalogRow,
} from '@/hooks/useTomVendingInventory';
import { useTomVendingMachinesList } from '@/hooks/useTomVendingMachine';
import TomsVendingMachineInventorySection from './TomsVendingMachineInventorySection';
import { formatTomVendingLastAudit } from '@/utils/tomVendingLastAudit';

const PAGE_SIZE = 25;

interface TomsVendingLocationInventorySectionProps {
  locationId: number;
  /** When true, "All" tab lists warehouse-bin stock for this hub (not vending machine totals). */
  isWarehouseHub?: boolean;
}

function machineTabLabel(assetId: string, title: string | null | undefined): string {
  const t = title?.trim();
  if (t) return `${assetId} — ${t}`;
  return assetId;
}

const TomsVendingLocationInventorySection: React.FC<TomsVendingLocationInventorySectionProps> = ({
  locationId,
  isWarehouseHub = false,
}) => {
  const { data: machines = [], isLoading: machinesLoading } = useTomVendingMachinesList(locationId);
  const sortedMachines = useMemo(
    () => [...machines].sort((a, b) => a.sortOrder - b.sortOrder || a.assetId.localeCompare(b.assetId)),
    [machines],
  );

  /** `'all'` or vending machine id as string */
  const [tabKey, setTabKey] = useState<string>('all');

  const selectedMachine =
    tabKey === 'all' ? null : machines.find((m) => String(m.id) === tabKey) ?? null;

  const [data, setData] = useState<LocationCatalogRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'id', desc: true }]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const [warehouseTarget, setWarehouseTarget] = useState<LocationAdjustmentWarehouseTarget | null>(null);

  useEffect(() => {
    if (!isWarehouseHub) {
      setWarehouseTarget(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const t = await fetchLocationAdjustmentTargets(locationId);
        if (!cancelled) setWarehouseTarget(t.warehouse);
      } catch {
        if (!cancelled) setWarehouseTarget(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locationId, isWarehouseHub]);

  const { showError } = useNotification();

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (tabKey === 'all' || machinesLoading) return;
    const mid = parseInt(tabKey, 10);
    if (Number.isNaN(mid) || !machines.some((m) => m.id === mid)) {
      setTabKey('all');
    }
  }, [tabKey, machines, machinesLoading]);

  const fetchCatalog = useCallback(async () => {
    if (tabKey !== 'all') return;
    setIsLoading(true);
    try {
      const r = await queryLocationInventoryCatalog({
        locationId,
        skip: pagination.pageIndex * pagination.pageSize,
        take: pagination.pageSize,
        search: debouncedSearch,
        orderBy: sorting[0]?.id || 'id',
        orderDirection: sorting[0]?.desc ? 'DESC' : 'ASC',
      });
      setData(r.data);
      setTotalRows(r.total);
    } catch (e: any) {
      showError(e?.message || 'Failed to load location inventory');
      setData([]);
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [tabKey, locationId, pagination, sorting, debouncedSearch, tableRefreshKey, showError]);

  useEffect(() => {
    void fetchCatalog();
  }, [fetchCatalog]);

  const columns = useMemo<MRT_ColumnDef<LocationCatalogRow>[]>(
    () => [
      {
        id: 'thumbnail',
        header: '',
        size: 56,
        enableSorting: false,
        Cell: ({ row }) => {
          const src = row.original.thumbnailUrl || row.original.imageUrl;
          return src ? (
            <Avatar src={src} variant="rounded" sx={{ width: 40, height: 40 }} />
          ) : (
            <Avatar variant="rounded" sx={{ width: 40, height: 40 }}>
              <Inventory2Icon fontSize="small" />
            </Avatar>
          );
        },
      },
      { accessorKey: 'upc', header: 'UPC' },
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ cell }) => (cell.getValue() as string) || '—',
      },
      {
        accessorKey: 'totalAtLocation',
        header: isWarehouseHub ? 'Qty (warehouse)' : 'Qty (all machines)',
        size: 140,
      },
    ],
    [isWarehouseHub],
  );

  const handleTabChange = (_: React.SyntheticEvent, next: string) => {
    setTabKey(next);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setSearch('');
    setDebouncedSearch('');
    if (next === 'all') {
      setTableRefreshKey((k) => k + 1);
    }
  };

  const machineSettingsUrl =
    selectedMachine != null
      ? `/app/toms-vending/locations/${locationId}/machines/${selectedMachine.id}`
      : null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 0.5, fontWeight: 600 }}>
        Inventory at this location
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isWarehouseHub
          ? 'Warehouse SKUs with stock in this hub’s storage bin. Manage precise quantities from Stock adjustment or the main Inventory page.'
          : 'View totals across every machine here, or pick a vending machine tab to add items and adjust quantities for that machine only. Warehouse stock is managed from the main Inventory page.'}
      </Typography>

      {isWarehouseHub && warehouseTarget ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Last warehouse audit:{' '}
          {formatTomVendingLastAudit(warehouseTarget.lastAuditAt, warehouseTarget.lastAuditByDisplayName)}
        </Typography>
      ) : null}

      <Tabs
        value={tabKey}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 2,
          minHeight: 42,
          '& .MuiTab-root': { textTransform: 'none', minHeight: 42, py: 1 },
        }}
      >
        <Tab label="All" value="all" />
        {sortedMachines.map((m) => (
          <Tab key={m.id} label={machineTabLabel(m.assetId, m.title)} value={String(m.id)} />
        ))}
      </Tabs>

      {tabKey === 'all' ? (
        <MaterialReactTable
          columns={columns}
          data={data}
          manualPagination
          manualSorting
          enableGlobalFilter={false}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          rowCount={totalRows}
          state={{ isLoading, pagination, sorting }}
          initialState={{ density: 'compact' }}
          renderTopToolbarCustomActions={() => (
            <TextField
              placeholder="Search UPC, name…"
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
              sx={{ width: 280 }}
            />
          )}
        />
      ) : selectedMachine ? (
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Managing stock on{' '}
              <strong>
                {selectedMachine.assetId}
                {selectedMachine.title?.trim() ? ` (${selectedMachine.title.trim()})` : ''}
              </strong>
              .
            </Typography>
            {machineSettingsUrl && (
              <Link
                component={RouterLink}
                to={machineSettingsUrl}
                variant="body2"
                sx={{ whiteSpace: 'nowrap' }}
              >
                Machine details…
              </Link>
            )}
          </Box>
          <TomsVendingMachineInventorySection
            locationId={locationId}
            machineId={selectedMachine.id}
            onBalancesChanged={() => setTableRefreshKey((k) => k + 1)}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default TomsVendingLocationInventorySection;
